import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, Shield, User, Trash2, Plus, AlertTriangle } from "lucide-react";

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "student" });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.user_id}`, { withCredentials: true });
      toast.success(`User ${userToDelete.email} deleted successfully`);
      setUsers(prev => prev.filter(u => u.user_id !== userToDelete.user_id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Please fill all required fields");
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
      toast.success("User created successfully");
      setUsers(prev => [...prev, { ...response.data, picture: null }]);
      setCreateDialogOpen(false);
      setNewUser({ name: "", email: "", password: "", role: "student" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const canDeleteUser = (targetUser) => {
    // Can't delete yourself
    if (currentUser?.user_id === targetUser.user_id) return false;
    // Superadmins can delete anyone except themselves
    if (currentUser?.role === 'superadmin') return true;
    // Admins can only delete students
    if (currentUser?.role === 'admin' && targetUser.role === 'student') return true;
    return false;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-users">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Manage Users
            </h1>
            <p className="text-stone-600 mt-1">View and manage user accounts</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-700 hover:bg-violet-800 rounded-full" data-testid="create-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Create New User</DialogTitle>
                <DialogDescription>Add a new student or admin account</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    id="password" 
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating} className="bg-violet-700 hover:bg-violet-800">
                    {creating ? "Creating..." : "Create User"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-violet-700" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Users</p>
                <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                  {users.length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <User className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Students</p>
                <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                  {users.filter(u => u.role === 'student').length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Admins</p>
                <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                  {users.filter(u => u.role === 'admin' || u.role === 'superadmin').length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 rounded-xl"
              data-testid="search-users-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-40 h-12 rounded-xl" data-testid="role-filter">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="student">Students</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
              <SelectItem value="superadmin">Superadmins</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users List */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardContent className="p-0">
            {filteredUsers.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-stone-900 mb-2">No users found</h3>
                <p className="text-stone-500">Try adjusting your search</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {filteredUsers.map((user) => (
                  <div 
                    key={user.user_id}
                    className="p-4 flex items-center gap-4 hover:bg-stone-50 transition-colors"
                    data-testid={`user-row-${user.user_id}`}
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-violet-100 text-violet-700">
                        {user.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900">{user.name}</p>
                      <p className="text-sm text-stone-500 truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {user.role !== 'superadmin' && (
                        <Select 
                          value={user.role} 
                          onValueChange={(value) => handleRoleChange(user.user_id, value)}
                        >
                          <SelectTrigger className="w-32" data-testid={`role-select-${user.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <Badge className={`${
                        user.role === 'superadmin'
                          ? 'bg-purple-100 text-purple-700'
                          : user.role === 'admin' 
                            ? 'bg-violet-100 text-violet-700' 
                            : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {user.role}
                      </Badge>
                      {canDeleteUser(user) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(user)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Outfit' }}>
                <AlertTriangle className="h-5 w-5" />
                Delete User
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                This action cannot be undone and will remove all their data including enrollments, submissions, and certificates.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? "Deleting..." : "Delete User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminUsers;
